import routes from '@config/routes';
import { serverFetcher } from '@instance';
import {
  getApiLeagueCode,
  getComparableLeagueCodes,
} from '@utils/config/leagues';
import { getCanonicalLeagueTeamSlug } from '@utils/navigation/league-public-paths';
import kebabCase from '@utils/kebab';
import CFL_COLUMN_DATA from '@v2/services/getTeamsByLeague/CFL.json';
import EUROPEAN_SOCCER_DATA from '@v2/services/getTeamsByLeague/EUROPEAN-SOCCER.json';
import FORMULA1_COLUMN_DATA from '@v2/services/getTeamsByLeague/FORMULA1.json';
import MLS_COLUMN_DATA from '@v2/services/getTeamsByLeague/MLS.json';
import NCAAB_COLUMN_DATA from '@v2/services/getTeamsByLeague/NCAAB.json';
import NCAAF_COLUMN_DATA from '@v2/services/getTeamsByLeague/NCAAF.json';
import NASCAR_COLUMN_DATA from '@v2/services/getTeamsByLeague/NASCAR.json';
import NHL_COLUMN_DATA from '@v2/services/getTeamsByLeague/NHL.json';
import PGA_COLUMN_DATA from '@v2/services/getTeamsByLeague/PGA.json';
import PWHL_COLUMN_DATA from '@v2/services/getTeamsByLeague/PWHL.json';
import RODEO_COLUMN_DATA from '@v2/services/getTeamsByLeague/RODEO.json';
import TENNIS_COLUMN_DATA from '@v2/services/getTeamsByLeague/TENNIS.json';
import WNBA_COLUMN_DATA from '@v2/services/getTeamsByLeague/WNBA.json';
import WORLDCUP_COLUMN_DATA from '@v2/services/getTeamsByLeague/WORLDCUP.json';
import WWE_COLUMN_DATA from '@v2/services/getTeamsByLeague/WWE.json';
import { getDivisionSortRuleForLeague } from '@v2/views/events/constants/leagueTeamListRules';

const NBA_DIVISION_ORDER = [
  'Atlantic',
  'Central',
  'Southeast',
  'Northwest',
  'Pacific',
  'Southwest',
] as const;

const normalizeTeamForLeague = (team, apiLeagueCode: string) => {
  // The feed still lists Utah under its 2024-25 name.
  if (
    apiLeagueCode.toLowerCase() === 'nhl' &&
    team.team === 'Utah Hockey Club'
  ) {
    return { ...team, team: 'Utah Mammoth' };
  }

  if (apiLeagueCode.toLowerCase() !== 'mlb') {
    return team;
  }

  if (team.team !== 'Oakland Athletics') {
    return team;
  }

  return {
    ...team,
    team: 'Athletics',
    mascot: 'Athletics',
    location: null,
  };
};

const mapStaticColumnData = (
  columnData,
  leagueCode: string,
  leagueLabel: string,
  uniqueTeams
) => {
  const teamsBySlug = new Map(
    uniqueTeams.map((team) => [
      getCanonicalLeagueTeamSlug(leagueCode, kebabCase(team.team)),
      team,
    ])
  );

  return columnData.map((group) => ({
    division: group.title || '',
    teams: group.items
      .map((item) => {
        const teamSlug =
          item.slug ||
          getCanonicalLeagueTeamSlug(leagueCode, kebabCase(item.team));
        return (
          teamsBySlug.get(teamSlug) || {
            ...item,
            team: item.team,
            league: leagueLabel,
            conference: group.title,
          }
        );
      })
      .filter(Boolean),
  }));
};

export const getTeamsByLeague = async (leagueName: string) => {
  const response = await serverFetcher(routes.getHeaderTeams, {
    method: 'GET',
  });

  // NCAA can arrive as either legacy API codes or URL slugs.
  const apiLeagueCode = getApiLeagueCode(leagueName);
  const europeanLeague = EUROPEAN_SOCCER_DATA.leagues.find(
    (league) => league.slug === apiLeagueCode.toLowerCase()
  );

  if (europeanLeague) {
    const europeanTeams = Array.isArray(response)
      ? response.filter(
          (team) => team.league?.toLowerCase() === apiLeagueCode.toLowerCase()
        )
      : [];

    return mapStaticColumnData(
      europeanLeague.groups,
      apiLeagueCode,
      europeanLeague.title,
      europeanTeams
    );
  }

  if (!response) return [];
  const comparableLeagueCodes = new Set(getComparableLeagueCodes(leagueName));

  const filteredTeamsByLeague = response.filter((team) => {
    return comparableLeagueCodes.has(team.league.toLowerCase());
  });

  const normalizedTeams = filteredTeamsByLeague.map((team) =>
    normalizeTeamForLeague(team, apiLeagueCode)
  );
  const seenTeams = new Set();
  const uniqueTeamsByLeague = normalizedTeams.filter((team) => {
    const teamKey = [
      team.league,
      team.conference,
      team.division,
      team.abbreviation,
      team.team,
    ]
      .map((value) => String(value || ''))
      .join('::');

    if (seenTeams.has(teamKey)) {
      return false;
    }

    seenTeams.add(teamKey);
    return true;
  });

  if (apiLeagueCode.toLowerCase() === 'mls') {
    return mapStaticColumnData(
      MLS_COLUMN_DATA,
      'mls',
      'MLS',
      uniqueTeamsByLeague
    );
  }

  // /teams still lists Arizona Coyotes and Utah Hockey Club; NHL.json has the current 32 teams.
  if (apiLeagueCode.toLowerCase() === 'nhl') {
    return mapStaticColumnData(
      NHL_COLUMN_DATA,
      'nhl',
      'NHL',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'wnba') {
    return mapStaticColumnData(
      WNBA_COLUMN_DATA,
      'wnba',
      'WNBA',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'tennis') {
    return mapStaticColumnData(
      TENNIS_COLUMN_DATA,
      'tennis',
      'Tennis',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'wwe') {
    return mapStaticColumnData(
      WWE_COLUMN_DATA,
      'wwe',
      'WWE',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'pga') {
    return mapStaticColumnData(
      PGA_COLUMN_DATA,
      'pga',
      'PGA',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'cfl') {
    return mapStaticColumnData(
      CFL_COLUMN_DATA,
      'cfl',
      'CFL',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'pwhl') {
    return mapStaticColumnData(
      PWHL_COLUMN_DATA,
      'pwhl',
      'PWHL',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'rodeo') {
    return mapStaticColumnData(
      RODEO_COLUMN_DATA,
      'rodeo',
      'Rodeo',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'world-cup') {
    return mapStaticColumnData(
      WORLDCUP_COLUMN_DATA,
      'world-cup',
      'World Cup',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'ncaaf') {
    return mapStaticColumnData(
      NCAAF_COLUMN_DATA,
      'ncaa-mens-football',
      'ncaa-mens-football',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'ncaab') {
    return mapStaticColumnData(
      NCAAB_COLUMN_DATA,
      'ncaa-mens-basketball',
      'ncaa-mens-basketball',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'nascar') {
    return mapStaticColumnData(
      NASCAR_COLUMN_DATA,
      'nascar',
      'NASCAR',
      uniqueTeamsByLeague
    );
  }

  if (apiLeagueCode.toLowerCase() === 'formula-1') {
    return mapStaticColumnData(
      FORMULA1_COLUMN_DATA,
      'formula-1',
      'Formula 1',
      uniqueTeamsByLeague
    );
  }

  const combineTeamsByDivision = uniqueTeamsByLeague.reduce((acc, team) => {
    if (team.conference && team.division) {
      const conferenceInitials =
        team.league === 'NFL'
          ? team.conference
          : team.conference
              .split(' ')
              .map((word) => word[0])
              .join('');

      const position =
        team.league === 'NBA'
          ? team.division
          : conferenceInitials?.length > 1
            ? `${conferenceInitials} ${team.division}`
            : team.division;

      if (!acc[position]) {
        acc[position] = [];
      }

      acc[position] = [...acc[position], team];
      return acc;
    }

    if (team.conference) {
      if (!acc[team.conference]) {
        acc[team.conference] = [];
      }
      acc[team.conference] = [...acc[team.conference], team];
      return acc;
    }
    if (team.division) {
      if (!acc[team.division]) {
        acc[team.division] = [];
      }
      acc[team.division] = [...acc[team.division], team];
      return acc;
    }
    return acc;
  }, {});

  const sorted = Object.keys(combineTeamsByDivision).map((key) => {
    return {
      division: key,
      teams: combineTeamsByDivision[key],
    };
  });

  const divisionSort = getDivisionSortRuleForLeague(apiLeagueCode);
  if (divisionSort === 'afcBeforeNfc') {
    sorted.sort((a, b) => {
      const aIsAfc = a.division.startsWith('AFC');
      const bIsAfc = b.division.startsWith('AFC');
      if (aIsAfc && !bIsAfc) return -1;
      if (!aIsAfc && bIsAfc) return 1;
      return a.division.localeCompare(b.division);
    });
  }

  if (apiLeagueCode.toLowerCase() === 'nba') {
    sorted.sort((a, b) => {
      return (
        NBA_DIVISION_ORDER.indexOf(
          a.division as (typeof NBA_DIVISION_ORDER)[number]
        ) -
        NBA_DIVISION_ORDER.indexOf(
          b.division as (typeof NBA_DIVISION_ORDER)[number]
        )
      );
    });
  }

  return sorted;
};
